import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Edit, ArrowLeft, FileDown, FileUp } from "lucide-react";
import * as XLSX from 'xlsx';
import { useLocation } from "wouter";

export default function PriceTables() {
  const [, setLocation] = useLocation();
  const { user, loading: authLoading } = useGoogleAuth();
  
  // Estados principais
  const [searchTerm, setSearchTerm] = useState("");
  const [emitenteIdFiltro, setEmitenteIdFiltro] = useState<number | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);
  const [showItemsDialog, setShowItemsDialog] = useState(false);
  
  // Estados do formulário de tabela
  const [nome, setNome] = useState("");
  const [somarImposto, setSomarImposto] = useState(false);
  const [descontoComercial, setDescontoComercial] = useState("0");
  const [addMode, setAddMode] = useState<"all" | "manual">("all");
  const [formEmitenteId, setFormEmitenteId] = useState<number | null>(null);
  
  // Estados para adicionar item inline
  const [newItemCodigo, setNewItemCodigo] = useState("");
  const [newItemDescricao, setNewItemDescricao] = useState("");
  const [newItemValor, setNewItemValor] = useState("");
  const [newItemProductId, setNewItemProductId] = useState<number | null>(null);
  
  // Estados para editar valor inline
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");
  
  // Estado para importação Excel
  const [importFile, setImportFile] = useState<File | null>(null);
  
  // Estados de paginação da lista de tabelas
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;
  
  // Estados de busca e paginação de itens
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [itemCurrentPage, setItemCurrentPage] = useState(1);
  const itemsPerPageInTable = 10;
  
  const emitenteIdConsulta = user?.role === "cliente"
    ? user.emitenteId ?? undefined
    : emitenteIdFiltro;

  // Queries
  const { data: priceTables = [], refetch: refetchTables } = trpc.priceTables.list.useQuery(
    emitenteIdConsulta ? { emitenteId: emitenteIdConsulta } : undefined
  );
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const { data: products = [] } = trpc.products.list.useQuery();
  
  // Query de itens (só carrega quando uma tabela está selecionada)
  const { data: tableItems = [], refetch: refetchItems } = trpc.priceTables.getItems.useQuery(
    { priceTableId: selectedTableId! },
    { enabled: !!selectedTableId }
  );
  
  // Mutations
  const createMutation = trpc.priceTables.create.useMutation({
    onSuccess: async (result) => {
      toast.success("Tabela criada com sucesso");
      
      // Se modo "all", adicionar todos os produtos do emitente selecionado
      const emitenteParaAdd = formEmitenteId;
      if (addMode === "all" && emitenteParaAdd) {
        try {
          await addAllProductsMutation.mutateAsync({
            priceTableId: result.id,
            emitenteId: emitenteParaAdd,
          });
          toast.success("Produtos adicionados à tabela");
        } catch (error: any) {
          toast.error(error.message || "Erro ao adicionar produtos");
        }
      }
      
      refetchTables();
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao criar tabela");
    },
  });
  
  const updateMutation = trpc.priceTables.update.useMutation({
    onSuccess: () => {
      toast.success("Tabela atualizada com sucesso");
      refetchTables();
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar tabela");
    },
  });
  
  const deleteMutation = trpc.priceTables.delete.useMutation({
    onSuccess: () => {
      toast.success("Tabela deletada com sucesso");
      refetchTables();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao deletar tabela");
    },
  });
  
  const addAllProductsMutation = trpc.priceTables.addAllProducts.useMutation();
  
  const deleteItemMutation = trpc.priceTables.deleteItem.useMutation({
    onSuccess: () => {
      toast.success("Item removido");
      refetchItems();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao remover item");
    },
  });
  
  const updateItemMutation = trpc.priceTables.updateItem.useMutation({
    onSuccess: () => {
      toast.success("Item atualizado");
      refetchItems();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar item");
    },
  });
  
  const createItemMutation = trpc.priceTables.createItem.useMutation({
    onSuccess: () => {
      toast.success("Item adicionado com sucesso");
      refetchItems();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao adicionar item");
    },
  });
  
  // Resetar para página 1 quando busca mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, emitenteIdFiltro]);
  
  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);
  
  // Loading check
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }
  
  if (!user) {
    return null;
  }
  
  // Apenas líderes e admins podem acessar
  
  // Filtrar tabelas por busca
  const filteredTables = priceTables.filter((table: any) => {
    const search = searchTerm.toLowerCase();
    return (
      table.nome.toLowerCase().includes(search) ||
      table.id.toString().includes(search)
    );
  });
  
  // Calcular paginação
  const totalPages = Math.ceil(filteredTables.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTables = filteredTables.slice(startIndex, endIndex);
  
  const handleOpenCreate = () => {
    setDialogMode("create");
    setEditingId(null);
    setNome("");
    setSomarImposto(false);
    setDescontoComercial("0");
    setAddMode("all");
    // Para clientes, pré-preencher com o emitenteId do usuário
    setFormEmitenteId(user?.emitenteId ?? null);
    setIsDialogOpen(true);
  };
  
  const handleOpenEdit = (table: any) => {
    setDialogMode("edit");
    setEditingId(table.id);
    setNome(table.nome);
    setFormEmitenteId(table.emitenteId ?? user?.emitenteId ?? null);
    setSomarImposto(!!table.somarImposto);
    setDescontoComercial(table.descontoComercial.toString());
    setIsDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingId(null);
    setNome("");
    setSomarImposto(false);
    setDescontoComercial("0");
    setAddMode("all");
    setFormEmitenteId(null);
  };
  
  const handleSubmit = async () => {
    if (!nome.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    
    // Usar o emitente selecionado no formulário
    const resolvedEmitenteId = formEmitenteId;
    
    if (!resolvedEmitenteId) {
      toast.error("Selecione um emitente");
      return;
    }
    
    const data = {
      nome: nome.trim(),
      emitenteId: resolvedEmitenteId,
      somarImposto,
      descontoComercial: parseFloat(descontoComercial) || 0,
    };
    
    if (dialogMode === "create") {
      await createMutation.mutateAsync({
        ...data,
        usarTodosItens: addMode === "all",
      });
    } else if (editingId) {
      await updateMutation.mutateAsync({
        id: editingId,
        ...data,
      });
    }
  };
  
  const handleDelete = async (id: number) => {
    if (confirm("Tem certeza que deseja deletar esta tabela? Todos os itens serão removidos.")) {
      await deleteMutation.mutateAsync({ id });
    }
  };
  
  const handleOpenItems = (tableId: number) => {
    setSelectedTableId(tableId);
    setShowItemsDialog(true);
  };
  
  const handleCloseItems = () => {
    setShowItemsDialog(false);
    setSelectedTableId(null);
  };
  
  const handleDeleteItem = async (itemId: number) => {
    if (confirm("Tem certeza que deseja remover este item?")) {
      await deleteItemMutation.mutateAsync({ id: itemId });
    }
  };
  
  const handleUpdateItemValue = async (itemId: number, newValue: number) => {
    await updateItemMutation.mutateAsync({
      id: itemId,
      valor: newValue,
      valorComImpostos: newValue, // Por enquanto igual, será calculado depois
    });
  };
  
  // Buscar produto por código ao pressionar Enter
  const handleSearchProductByCodigo = async (codigo: string) => {
    if (!codigo.trim()) return;
    
    // Buscar tabela selecionada para pegar emitenteId
    const selectedTable = priceTables.find((t: any) => t.id === selectedTableId);
    if (!selectedTable) {
      toast.error("Tabela não encontrada");
      return;
    }
    
    // Buscar produto pelo código e emitenteId
    const product = products.find(
      (p: any) => p.cod.toLowerCase() === codigo.toLowerCase() && p.emitenteId === selectedTable.emitenteId
    );
    
    if (!product) {
      toast.error(`Produto com código "${codigo}" não encontrado para este emitente`);
      return;
    }
    
    // Preencher campos automaticamente
    setNewItemProductId(product.id);
    setNewItemDescricao(product.descricao);
    setNewItemValor((product.venda || 0).toString());
    toast.success(`Produto encontrado: ${product.descricao}`);
  };
  
  const handleAddItem = async () => {
    if (!newItemCodigo.trim()) {
      toast.error("Digite o código do produto");
      return;
    }
    
    if (!newItemProductId) {
      toast.error("Produto não encontrado. Pressione Enter após digitar o código.");
      return;
    }
    
    const valor = parseFloat(newItemValor) || 0;
    
    try {
      await createItemMutation.mutateAsync({
        priceTableId: selectedTableId!,
        productId: newItemProductId,
        codigo: newItemCodigo,
        descricao: newItemDescricao,
        valor,
        valorComImpostos: valor, // Por enquanto igual
      });
      
      // Limpar formulário apenas após sucesso
      setNewItemCodigo("");
      setNewItemDescricao("");
      setNewItemValor("");
      setNewItemProductId(null);
    } catch (error) {
      // Erro já tratado no onError da mutation
    }
  };
  
  const handleStartEditValue = (itemId: number, currentValue: string) => {
    setEditingItemId(itemId);
    setEditingValue(currentValue);
  };
  
  const handleSaveEditValue = async (itemId: number) => {
    const newValue = parseFloat(editingValue);
    if (isNaN(newValue) || newValue < 0) {
      toast.error("Valor inválido");
      return;
    }
    
    await updateItemMutation.mutateAsync({
      id: itemId,
      valor: newValue,
      valorComImpostos: newValue, // Por enquanto igual
    });
    
    setEditingItemId(null);
    setEditingValue("");
  };
  
  const handleCancelEditValue = () => {
    setEditingItemId(null);
    setEditingValue("");
  };
  
  // Exportar itens para Excel
  const handleExportExcel = () => {
    if (tableItems.length === 0) {
      toast.error("Nenhum item para exportar");
      return;
    }

    const selectedTable = priceTables.find((t: any) => t.id === selectedTableId);
    
    // Preparar dados para exportação
    const data = tableItems.map((item: any) => ({
      'Código': item.codigo,
      'Descrição': item.descricao,
      'Valor': item.valor,
      'Valor c/ Impostos': item.valorComImpostos,
    }));

    // Criar workbook e worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);

    // Adicionar worksheet ao workbook
    XLSX.utils.book_append_sheet(wb, ws, "Itens");

    // Gerar arquivo e fazer download
    const fileName = `tabela_${selectedTable?.nome || 'preco'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);

    toast.success("Arquivo Excel exportado com sucesso");
  };

  // Importar itens do Excel
  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        toast.error("Arquivo Excel vazio");
        return;
      }

      // Validar estrutura do arquivo
      const firstRow: any = jsonData[0];
      if (!firstRow['Código'] || !firstRow['Descrição'] || !firstRow['Valor']) {
        toast.error("Arquivo Excel inválido. Colunas necessárias: Código, Descrição, Valor");
        return;
      }

      // Processar cada linha
      let successCount = 0;
      let errorCount = 0;

      for (const row: any of jsonData) {
        try {
          // Buscar produto pelo código
          const product = products.find((p: any) => p.cod === row['Código']);
          
          if (!product) {
            console.warn(`Produto não encontrado: ${row['Código']}`);
            errorCount++;
            continue;
          }

          // Adicionar item
          await createItemMutation.mutateAsync({
            priceTableId: selectedTableId!,
            productId: product.id,
            codigo: row['Código'],
            descricao: row['Descrição'],
            valor: parseFloat(row['Valor']) || 0,
            valorComImpostos: parseFloat(row['Valor c/ Impostos'] || row['Valor']) || 0,
          });

          successCount++;
        } catch (error) {
          console.error(`Erro ao importar item ${row['Código']}:`, error);
          errorCount++;
        }
      }

      // Feedback
      if (successCount > 0) {
        toast.success(`${successCount} itens importados com sucesso`);
        refetchItems();
      }
      
      if (errorCount > 0) {
        toast.warning(`${errorCount} itens não puderam ser importados`);
      }

      // Limpar input
      e.target.value = '';
      
    } catch (error) {
      console.error("Erro ao importar Excel:", error);
      toast.error("Erro ao processar arquivo Excel");
    }
  };
  


  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Tabelas de Preço</h1>
            <p className="text-muted-foreground">
              Gerencie tabelas de preço e seus itens
            </p>
          </div>
        </div>

        {/* Filtros e ações */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <Input
                placeholder="Buscar por nome ou ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
              {user?.role !== "cliente" && (
                <Select
                  value={emitenteIdFiltro ? String(emitenteIdFiltro) : "todos"}
                  onValueChange={(value) => setEmitenteIdFiltro(value === "todos" ? undefined : Number(value))}
                >
                  <SelectTrigger className="sm:w-[260px]">
                    <SelectValue placeholder="Todos os emitentes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os emitentes</SelectItem>
                    {emitentes.map((emitente: any) => (
                      <SelectItem key={emitente.id} value={String(emitente.id)}>
                        {emitente.razaoSocial || emitente.nomeFantasia || `Emitente ${emitente.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button onClick={handleOpenCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Tabela
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabela de tabelas de preço */}
        <Card>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">ID</th>
                    <th className="text-left p-2">Nome</th>
                    <th className="text-left p-2">Data de Criação</th>
                    <th className="text-left p-2">Somar Imposto</th>
                    <th className="text-left p-2">Desconto %</th>
                    <th className="text-right p-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTables.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center p-8 text-muted-foreground">
                        Nenhuma tabela encontrada
                      </td>
                    </tr>
                  ) : (
                    paginatedTables.map((table: any) => (
                      <tr key={table.id} className="border-b hover:bg-muted/50">
                        <td className="p-2">{table.id}</td>
                        <td className="p-2 font-medium">{table.nome}</td>
                        <td className="p-2">
                          {new Date(table.criadoEm).toLocaleDateString("pt-BR")}
                        </td>
                        <td className="p-2">
                          {table.somarImposto ? (
                            <span className="text-green-600">Sim</span>
                          ) : (
                            <span className="text-muted-foreground">Não</span>
                          )}
                        </td>
                        <td className="p-2">{table.descontoComercial}%</td>
                        <td className="p-2">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenItems(table.id)}
                            >
                              Ver Itens
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenEdit(table)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(table.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Página {currentPage} de {totalPages} ({filteredTables.length} tabelas)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog de criar/editar tabela */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="!w-[80vw] !max-w-[80vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {dialogMode === "create" ? "Nova Tabela de Preço" : "Editar Tabela de Preço"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Emitente */}
              <div>
                <Label htmlFor="formEmitente">Emitente</Label>
                {user?.role === 'cliente' && dialogMode === "edit" ? (
                  <Input
                    id="formEmitente"
                    value={emitentes.find((e: any) => e.id === formEmitenteId)?.razaoSocial || emitentes.find((e: any) => e.id === formEmitenteId)?.nomeFantasia || `Emitente ${formEmitenteId}` || ''}
                    disabled
                    className="bg-muted"
                  />
                ) : (
                  <Select
                    value={formEmitenteId ? String(formEmitenteId) : ''}
                    onValueChange={(v) => setFormEmitenteId(Number(v))}
                  >
                    <SelectTrigger id="formEmitente">
                      <SelectValue placeholder="Selecione um emitente" />
                    </SelectTrigger>
                    <SelectContent>
                      {emitentes.map((e: any) => (
                        <SelectItem key={e.id} value={String(e.id)}>
                          {e.razaoSocial || e.nomeFantasia || `Emitente ${e.id}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Nome */}
              <div>
                <Label htmlFor="nome">Nome da Tabela</Label>
                <Input
                  id="nome"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex: Tabela Padrão 2026"
                />
              </div>

              {/* Somar Imposto */}
              <div>
                <Label htmlFor="somarImposto">Somar Imposto</Label>
                <Select
                  value={somarImposto ? "sim" : "nao"}
                  onValueChange={(v) => setSomarImposto(v === "sim")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sim">Sim</SelectItem>
                    <SelectItem value="nao">Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Desconto Comercial */}
              <div>
                <Label htmlFor="desconto">% Desconto Comercial</Label>
                <Input
                  id="desconto"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={descontoComercial}
                  onChange={(e) => setDescontoComercial(e.target.value)}
                  placeholder="0.00"
                />
              </div>

              {/* Modo de adição (só no create) */}
              {dialogMode === "create" && (
                <div>
                  <Label>Adicionar Produtos</Label>
                  <Select value={addMode} onValueChange={(v: any) => setAddMode(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        Usar todos os itens do emitente
                      </SelectItem>
                      <SelectItem value="manual">
                        Adicionar itens manualmente
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {addMode === "all"
                      ? "Todos os produtos do seu emitente serão adicionados automaticamente"
                      : "Você poderá adicionar produtos manualmente depois"}
                  </p>
                </div>
              )}

              {/* Botões */}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
                  {dialogMode === "create" ? "Criar" : "Salvar"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog de itens */}
        <Dialog open={showItemsDialog} onOpenChange={setShowItemsDialog}>
          <DialogContent className="!w-[80vw] !max-w-[80vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Itens da Tabela</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Botões de ação */}
              <div className="flex justify-between items-center flex-wrap gap-2">
                <div className="flex gap-2">
                  <Button onClick={handleExportExcel} size="sm" variant="outline">
                    <FileDown className="h-4 w-4 mr-2" />
                    Exportar Excel
                  </Button>
                  
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => document.getElementById('import-excel-input')?.click()}
                  >
                    <FileUp className="h-4 w-4 mr-2" />
                    Importar Excel
                  </Button>
                  
                  <input
                    id="import-excel-input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleImportExcel}
                    className="hidden"
                  />
                </div>
              </div>
              
              {/* Campo de busca de itens */}
              <div>
                <Input
                  placeholder="Buscar por código ou descrição..."
                  value={itemSearchTerm}
                  onChange={(e) => {
                    setItemSearchTerm(e.target.value);
                    setItemCurrentPage(1);
                  }}
                  className="max-w-md"
                />
              </div>
              
              {/* Formulário inline para adicionar item */}
              <div className="border rounded-lg p-4 bg-muted/30">
                <h3 className="text-sm font-medium mb-3">Adicionar Item</h3>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-2">
                    <Label htmlFor="newItemCodigo">Código</Label>
                    <Input
                      id="newItemCodigo"
                      value={newItemCodigo}
                      onChange={(e) => setNewItemCodigo(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchProductByCodigo(newItemCodigo);
                        }
                      }}
                      placeholder="Digite e pressione Enter"
                    />
                  </div>
                  <div className="col-span-5">
                    <Label htmlFor="newItemDescricao">Descrição</Label>
                    <Input
                      id="newItemDescricao"
                      value={newItemDescricao}
                      readOnly
                      placeholder="Preenche automaticamente"
                      className="bg-muted"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="newItemValor">Valor (R$)</Label>
                    <Input
                      id="newItemValor"
                      type="number"
                      step="0.01"
                      value={newItemValor}
                      onChange={(e) => setNewItemValor(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="col-span-3 flex items-end">
                    <Button onClick={handleAddItem} disabled={createItemMutation.isPending} className="w-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar
                    </Button>
                  </div>
                </div>
              </div>
              
              {(() => {
                // Filtrar itens pela busca
                const filteredItems = tableItems.filter((item: any) => {
                  const searchLower = itemSearchTerm.toLowerCase();
                  return (
                    item.codigo.toLowerCase().includes(searchLower) ||
                    item.descricao.toLowerCase().includes(searchLower)
                  );
                });
                
                // Paginar itens filtrados
                const totalPages = Math.ceil(filteredItems.length / itemsPerPageInTable);
                const startIndex = (itemCurrentPage - 1) * itemsPerPageInTable;
                const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPageInTable);
                
                if (tableItems.length === 0) {
                  return (
                    <div className="text-center p-8 text-muted-foreground">
                      Nenhum item nesta tabela. Digite um código acima e pressione Enter para começar.
                    </div>
                  );
                }
                
                if (filteredItems.length === 0) {
                  return (
                    <div className="text-center p-8 text-muted-foreground">
                      Nenhum item encontrado com "{itemSearchTerm}".
                    </div>
                  );
                }
                
                return (
                  <>
                    <div className="overflow-x-auto overflow-y-auto max-h-[400px]">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-background">
                          <tr className="border-b">
                            <th className="text-left p-2">Código</th>
                            <th className="text-left p-2">Descrição</th>
                            <th className="text-right p-2">Valor</th>
                            <th className="text-right p-2">Valor c/ Impostos</th>
                            <th className="text-right p-2">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedItems.map((item: any) => (
                        <tr key={item.id} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-mono">{item.codigo}</td>
                          <td className="p-2">{item.descricao}</td>
                          <td className="p-2 text-right">
                            {editingItemId === item.id ? (
                              <div className="flex items-center justify-end gap-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  className="w-24 text-right"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleSaveEditValue(item.id);
                                    } else if (e.key === "Escape") {
                                      handleCancelEditValue();
                                    }
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleSaveEditValue(item.id)}
                                >
                                  ✓
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={handleCancelEditValue}
                                >
                                  ✕
                                </Button>
                              </div>
                            ) : (
                              <span
                                className="cursor-pointer hover:bg-muted px-2 py-1 rounded"
                                onClick={() => handleStartEditValue(item.id, item.valor)}
                              >
                                R$ {parseFloat(item.valor).toFixed(2)}
                              </span>
                            )}
                          </td>
                          <td className="p-2 text-right">
                            R$ {parseFloat(item.valorComImpostos).toFixed(2)}
                          </td>
                          <td className="p-2">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStartEditValue(item.id, item.valor)}
                                title="Editar valor"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteItem(item.id)}
                                title="Excluir item"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Controles de paginação */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          Página {itemCurrentPage} de {totalPages} ({filteredItems.length} itens)
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setItemCurrentPage(p => Math.max(1, p - 1))}
                            disabled={itemCurrentPage === 1}
                          >
                            Anterior
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setItemCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={itemCurrentPage === totalPages}
                          >
                            Próxima
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              
              <div className="flex justify-end">
                <Button variant="outline" onClick={handleCloseItems}>
                  Fechar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>


      </div>
    </div>
  );
}
